'use client';

import React, { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';


interface FormData {
  cardType: 'Family' | 'Family3Month' | 'Personal' | 'Corporate';
  customerName: string;
  phoneNumber: string;
  nationalId: string;
  workLocation: string;
  email: string;
  discountCode: string;
  employeeCount: number;
}

interface FormErrors {
  customerName?: string;
  phoneNumber?: string;
  nationalId?: string;
  workLocation?: string;
  email?: string;
  discountCode?: string;
  employeeCount?: string;
}

const SubscriptionRegistrationInteractive = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    cardType: 'Family',
    customerName: '',
    phoneNumber: '',
    nationalId: '',
    workLocation: '',
    email: '',
    discountCode: '',
    employeeCount: 1,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [discountApplied, setDiscountApplied] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    setIsHydrated(true);
    const cardTypeParam = searchParams.get('type');
    if (cardTypeParam === 'Family' || cardTypeParam === 'Family3Month' || cardTypeParam === 'Personal' || cardTypeParam === 'Corporate') {
      setFormData((prev) => ({ ...prev, cardType: cardTypeParam as FormData['cardType'] }));
    }
  }, [searchParams]);

  const cardDetails = {
    Family: {
      name: t('جودة الحياة عائلي', 'Quality of Life Family'),
      price: 800,
      image: 'https://cmgesbyqfxkmwckadqpy.supabase.co/storage/v1/object/sign/cards/Copy%20of%20Black%20and%20Gold%20Classy%20Business%20Card%20(2).png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8xNWJhNmJlNC00YzUzLTQwZTItOGI4OC1mYjg1OWNlNzM1YTQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJjYXJkcy9Db3B5IG9mIEJsYWNrIGFuZCBHb2xkIENsYXNzeSBCdXNpbmVzcyBDYXJkICgyKS5wbmciLCJpYXQiOjE3Njk3MzYwMjEsImV4cCI6MTgwMTI3MjAyMX0.ImrlFBAKAQFyXTj3oHSxpwnn6HLb-YN2u6cwspSPw-A',
      color: 'bg-primary',
    },
    Family3Month: {
      name: t('جودة الحياة عائلي ربع سنوي', 'Quality of Life Family Quarterly'),
      price: 250,
      duration: t('3 أشهر فقط', '3 Months Only'),
      image: 'https://ocadeynpyrbhhpnmlizj.supabase.co/storage/v1/object/sign/123/79f1af79-1181-47d8-aab5-ea80271c243b.jfif?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV82ZDJlYmU2My03ZDcwLTQ1ODYtOWNhMC1mMWM2NjNiZjZkZDUiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiIxMjMvNzlmMWFmNzktMTE4MS00N2Q4LWFhYjUtZWE4MDI3MWMyNDNiLmpmaWYiLCJpYXQiOjE3NzE0MjA2OTcsImV4cCI6MTgwMjk1NjY5N30.XcajdrtHIc02v8uoPEULefJE0tiU4djawI4RTIxfkoE',
      color: 'bg-primary',
    },
    Personal: {
      name: t('جودة الحياة شخصي', 'Quality of Life Personal'),
      price: 150,
      duration: t('سنة كاملة', 'Full Year'),
      image: '/assets/images/WhatsApp_Image_2026-03-29_at_8.21.55_PM-1774805462574.jpeg',
      color: 'bg-emerald-600',
    },
    Corporate: {
      name: t('جودة الحياة شركات', 'Quality of Life Corporate'),
      price: 150, // per person
      duration: t('سنة كاملة', 'Full Year'),
      image: 'https://ocadeynpyrbhhpnmlizj.supabase.co/storage/v1/object/sign/123/WhatsApp%20Image%202026-04-25%20at%2012.38.41%20PM.jpeg?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV82ZDJlYmU2My03ZDcwLTQ1ODYtOWNhMC1mMWM2NjNiZjZkZDUiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiIxMjMvV2hhdHNBcHAgSW1hZ2UgMjAyNi0wNC0yNSBhdCAxMi4zOC40MSBQTS5qcGVnIiwiaWF0IjoxNzc3MTA5OTYxLCJleHAiOjE4MDg2NDU5NjF9.cWo1_g8SuflpVV_jnW7wLBIKZLOJV8_EGRhZyMvNx9E',
      color: 'bg-indigo-700',
    },
  };

  // Compute corporate total price
  const getCorporateTotal = () => {
    const count = Math.max(1, formData.employeeCount || 1);
    return count * 150;
  };

  const getEffectivePrice = () => {
    if (formData.cardType === 'Corporate') return getCorporateTotal();
    return cardDetails[formData.cardType].price;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.customerName.trim()) {
      newErrors.customerName = t('اسم العميل مطلوب', 'Customer name is required');
    } else if (formData.customerName.trim().length < 3) {
      newErrors.customerName = t('الاسم يجب أن يكون 3 أحرف على الأقل', 'Name must be at least 3 characters');
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = t('رقم الجوال مطلوب', 'Mobile number is required');
    } else if (!/^[+]?[0-9]{10,15}$/.test(formData.phoneNumber.replace(/\s/g, ''))) {
      newErrors.phoneNumber = t('رقم الجوال غير صحيح', 'Invalid mobile number');
    }

    if (!formData.nationalId.trim()) {
      newErrors.nationalId = t('رقم الهوية مطلوب', 'National ID is required');
    } else if (!/^[0-9]{10}$/.test(formData.nationalId.trim())) {
      newErrors.nationalId = t('رقم الهوية يجب أن يكون 10 أرقام', 'National ID must be 10 digits');
    }

    if (!formData.workLocation.trim()) {
      newErrors.workLocation = t('مكان العمل مطلوب', 'Work location is required');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('البريد الإلكتروني مطلوب', 'Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('البريد الإلكتروني غير صحيح', 'Invalid email address');
    }

    if (formData.cardType === 'Corporate') {
      if (!formData.employeeCount || formData.employeeCount < 1) {
        newErrors.employeeCount = t('يجب إدخال عدد الموظفين (1 على الأقل)', 'Employee count must be at least 1');
      }
    }

    // Validate discount code if provided
    if (formData.discountCode.trim()) {
      const code = formData.discountCode.trim().toUpperCase();
      const generalCodes = ['FOLI2311', 'AMIRA99', 'EBRAHIM33', 'ABDO777', 'AMIRA78', 'SAAD188', 'FARES77', 'ELBAZ95', 'MO99'];
      const isEbrahim444 = code === 'EBRAHIM444';
      const isGeneralCode = generalCodes.includes(code);
      const isBaladia5 = code === 'BALADIA5';
      const isLaban28 = code === 'LABAN28';
      const isGwda77 = code === 'GWDA77';

      if (!isGeneralCode && !isEbrahim444 && !isBaladia5 && !isLaban28 && !isGwda77) {
        newErrors.discountCode = t('كود الخصم غير صحيح', 'Invalid discount code');
      } else if (isEbrahim444 && formData.cardType !== 'Family') {
        newErrors.discountCode = t('هذا الكود مخصص لبطاقة العائلة 800 ريال فقط', 'This code is for the Family card (800 SAR) only');
      } else if (isLaban28 && formData.cardType !== 'Corporate') {
        newErrors.discountCode = t('كود LABAN28 مخصص لبطاقة الشركات فقط', 'LABAN28 code is for Corporate card only');
      }
      // GWDA77 is valid for all card types — no restriction needed
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    let updatedValue: string | number = value;

    if (name === 'employeeCount') {
      // Only allow digits
      const digitsOnly = value.replace(/[^0-9]/g, '');
      updatedValue = digitsOnly === '' ? 0 : parseInt(digitsOnly, 10);
    }

    setFormData((prev) => ({ ...prev, [name]: updatedValue }));

    // Check discount code validity in real-time
    if (name === 'discountCode') {
      const code = value.trim().toUpperCase();
      const generalCodes = ['FOLI2311', 'AMIRA99', 'EBRAHIM33', 'ABDO777', 'AMIRA78', 'SAAD188', 'FARES77', 'ELBAZ95', 'MO99'];
      const isEbrahim444 = code === 'EBRAHIM444';
      const isGeneralCode = generalCodes.includes(code);
      const isBaladia5 = code === 'BALADIA5';
      const isLaban28 = code === 'LABAN28';
      const isGwda77 = code === 'GWDA77';

      if (isLaban28 && formData.cardType === 'Corporate') {
        setDiscountApplied(true);
        setErrors((prev) => ({ ...prev, discountCode: undefined }));
      } else if (isLaban28 && formData.cardType !== 'Corporate') {
        setDiscountApplied(false);
        setErrors((prev) => ({ ...prev, discountCode: 'كود LABAN28 مخصص لبطاقة الشركات فقط' }));
      } else if (isGwda77 || isGeneralCode || (isEbrahim444 && formData.cardType === 'Family') || isBaladia5) {
        setDiscountApplied(true);
        setErrors((prev) => ({ ...prev, discountCode: undefined }));
      } else {
        setDiscountApplied(false);
      }
    }

    // Re-check discount when card type changes
    if (name === 'cardType') {
      const code = formData.discountCode.trim().toUpperCase();
      if (code === 'GWDA77') {
        setDiscountApplied(true);
        setErrors((prev) => ({ ...prev, discountCode: undefined }));
      } else if (code === 'LABAN28') {
        if (value === 'Corporate') {
          setDiscountApplied(true);
          setErrors((prev) => ({ ...prev, discountCode: undefined }));
        } else {
          setDiscountApplied(false);
          setErrors((prev) => ({ ...prev, discountCode: 'كود LABAN28 مخصص لبطاقة الشركات فقط' }));
        }
      } else if (code === 'EBRAHIM444' && value !== 'Family') {
        setDiscountApplied(false);
        setErrors((prev) => ({ ...prev, discountCode: 'هذا الكود مخصص لبطاقة العائلة 800 ريال فقط' }));
      } else if (code === 'EBRAHIM444' && value === 'Family') {
        setDiscountApplied(true);
        setErrors((prev) => ({ ...prev, discountCode: undefined }));
      } else if (code === 'BALADIA5') {
        setDiscountApplied(true);
        setErrors((prev) => ({ ...prev, discountCode: undefined }));
      }
    }

    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    await new Promise((resolve) => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setSubmitSuccess(true);

    const code = formData.discountCode.trim().toUpperCase();
    let originalPrice = getEffectivePrice();
    let discountAmount = 0;

    if (discountApplied) {
      if (code === 'GWDA77') {
        if (formData.cardType === 'Personal') {
          discountAmount = 50;
        } else if (formData.cardType === 'Corporate') {
          const count = Math.max(1, formData.employeeCount || 1);
          discountAmount = count * 50;
        } else {
          // Family, Family3Month, VIP — 50% off
          discountAmount = Math.round(originalPrice * 0.5);
        }
      } else if (code === 'LABAN28' && formData.cardType === 'Corporate') {
        // 50 SAR off per person
        const count = Math.max(1, formData.employeeCount || 1);
        discountAmount = count * 50;
      } else if (code === 'EBRAHIM444' && formData.cardType === 'Family') {
        discountAmount = 550;
      } else if (code === 'BALADIA5') {
        if (formData.cardType === 'Personal') {
          discountAmount = 50;
        } else {
          discountAmount = Math.round(originalPrice * 0.5);
        }
      } else {
        if (formData.cardType === 'Personal') {
          discountAmount = 50;
        } else {
          discountAmount = Math.round(originalPrice * 0.5);
        }
      }
    }

    const finalPrice = originalPrice - discountAmount;

    const invoiceData = {
      cardType: formData.cardType,
      customerName: formData.customerName,
      phoneNumber: formData.phoneNumber,
      nationalId: formData.nationalId,
      workLocation: formData.workLocation,
      email: formData.email,
      discountCode: code,
      discountApplied: discountApplied,
      originalPrice: originalPrice,
      discountAmount: discountAmount,
      finalPrice: finalPrice,
      employeeCount: formData.cardType === 'Corporate' ? formData.employeeCount : undefined,
      invoiceNumber: `INV-${Date.now()}`,
      invoiceDate: new Date().toISOString(),
    };

    setTimeout(() => {
      const queryParams = new URLSearchParams({
        data: JSON.stringify(invoiceData),
      });
      router.push(`/invoice-page?${queryParams.toString()}`);
    }, 1500);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-muted rounded w-1/2 mx-auto"></div>
            <div className="h-64 bg-muted rounded"></div>
            <div className="space-y-4">
              <div className="h-12 bg-muted rounded"></div>
              <div className="h-12 bg-muted rounded"></div>
              <div className="h-12 bg-muted rounded"></div>
              <div className="h-12 bg-muted rounded"></div>
              <div className="h-12 bg-muted rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentCard = cardDetails[formData.cardType];
  const effectivePrice = getEffectivePrice();

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Page Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-primary font-amiri mb-3">
            {t('تسجيل الاشتراك', 'Subscription Registration')}
          </h1>
          <p className="text-muted-foreground text-lg">
            {t('أكمل بياناتك للاشتراك في', 'Complete your details to subscribe to')} {currentCard.name}
          </p>
        </div>

        {/* Success Message */}
        {submitSuccess && (
          <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-3">
            <Icon name="CheckCircleIcon" size={24} className="text-success flex-shrink-0" />
            <div>
              <p className="text-success font-medium">{t('تم تسجيل اشتراكك بنجاح!', 'Your subscription was registered successfully!')}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {t('جاري تحويلك لإتمام عملية الدفع...', 'Redirecting you to complete payment...')}
              </p>
            </div>
          </div>
        )}

        {/* Registration Form */}
        <div className="bg-card rounded-xl shadow-lg border border-border p-6 md:p-8">
          <h2 className="text-2xl font-bold text-foreground font-amiri mb-6">
            {t('بيانات المشترك', 'Subscriber Information')}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Card Type Field */}
            <div>
              <label htmlFor="cardType" className="block text-sm font-medium text-foreground mb-2">
                {t('نوع البطاقة', 'Card Type')} <span className="text-error">*</span>
              </label>
              <select
                id="cardType"
                name="cardType"
                value={formData.cardType}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-premium"
              >
                <option value="Personal">{t('جودة الحياة شخصي (سنوية) - 150 ريال', 'Quality of Life Personal (Annual) - 150 SAR')}</option>
                <option value="Family">{t('جودة الحياة عائلي - 800 ريال', 'Quality of Life Family - 800 SAR')}</option>
                <option value="Family3Month">{t('جودة الحياة عائلي ربع سنوي - 250 ريال', 'Quality of Life Family Quarterly - 250 SAR')}</option>
                <option value="Corporate">{t('جودة الحياة شركات - 150 ريال / فرد', 'Quality of Life Corporate - 150 SAR / person')}</option>
              </select>
            </div>

            {/* Employee Count Field - Corporate only */}
            {formData.cardType === 'Corporate' && (
              <div>
                <label htmlFor="employeeCount" className="block text-sm font-medium text-foreground mb-2">
                  {t('عدد الموظفين', 'Number of Employees')} <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  id="employeeCount"
                  name="employeeCount"
                  value={formData.employeeCount === 0 ? '' : formData.employeeCount}
                  onChange={handleChange}
                  inputMode="numeric"
                  className={`w-full px-4 py-3 rounded-md border ${
                    errors.employeeCount ? 'border-error' : 'border-border'
                  } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-premium`}
                  placeholder={t('أدخل عدد الموظفين', 'Enter number of employees')}
                />
                {errors.employeeCount && (
                  <p className="mt-1 text-sm text-error flex items-center gap-1">
                    <Icon name="ExclamationCircleIcon" size={16} />
                    {errors.employeeCount}
                  </p>
                )}
                <p className="mt-1 text-sm text-muted-foreground">
                  {t('الإجمالي:', 'Total:')} {Math.max(1, formData.employeeCount || 1)} {t('موظف × 150 ريال =', 'employees × 150 SAR =')} <span className="font-bold text-foreground">{getCorporateTotal()} {t('ريال', 'SAR')}</span>
                </p>
              </div>
            )}

            {/* Customer Name Field */}
            <div>
              <label htmlFor="customerName" className="block text-sm font-medium text-foreground mb-2">
                {t('اسم العميل', 'Customer Name')} <span className="text-error">*</span>
              </label>
              <input
                type="text"
                id="customerName"
                name="customerName"
                value={formData.customerName}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-md border ${
                  errors.customerName ? 'border-error' : 'border-border'
                } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-premium`}
                placeholder={t('أدخل اسمك الكامل', 'Enter your full name')}
              />
              {errors.customerName && (
                <p className="mt-1 text-sm text-error flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {errors.customerName}
                </p>
              )}
            </div>

            {/* Phone Number Field */}
            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-foreground mb-2">
                {t('رقم الجوال', 'Mobile Number')} <span className="text-error">*</span>
              </label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-md border ${
                  errors.phoneNumber ? 'border-error' : 'border-border'
                } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-premium`}
                placeholder="+966 50 123 4567"
              />
              {errors.phoneNumber && (
                <p className="mt-1 text-sm text-error flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {errors.phoneNumber}
                </p>
              )}
            </div>

            {/* National ID Field */}
            <div>
              <label htmlFor="nationalId" className="block text-sm font-medium text-foreground mb-2">
                {t('رقم الهوية', 'National ID')} <span className="text-error">*</span>
              </label>
              <input
                type="text"
                id="nationalId"
                name="nationalId"
                value={formData.nationalId}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-md border ${
                  errors.nationalId ? 'border-error' : 'border-border'
                } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-premium`}
                placeholder={t('أدخل رقم الهوية الوطنية (10 أرقام)', 'Enter national ID number (10 digits)')}
                maxLength={10}
                inputMode="numeric"
              />
              {errors.nationalId && (
                <p className="mt-1 text-sm text-error flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {errors.nationalId}
                </p>
              )}
            </div>

            {/* Work Location Field */}
            <div>
              <label htmlFor="workLocation" className="block text-sm font-medium text-foreground mb-2">
                {t('مكان العمل', 'Work Location')} <span className="text-error">*</span>
              </label>
              <input
                type="text"
                id="workLocation"
                name="workLocation"
                value={formData.workLocation}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-md border ${
                  errors.workLocation ? 'border-error' : 'border-border'
                } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-premium`}
                placeholder={t('أدخل مكان عملك', 'Enter your work location')}
              />
              {errors.workLocation && (
                <p className="mt-1 text-sm text-error flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {errors.workLocation}
                </p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                {t('البريد الإلكتروني', 'Email Address')} <span className="text-error">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-md border ${
                  errors.email ? 'border-error' : 'border-border'
                } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-premium`}
                placeholder="example@email.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-error flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Discount Code Field */}
            <div>
              <label htmlFor="discountCode" className="block text-sm font-medium text-foreground mb-2">
                {t('كود الخصم (اختياري)', 'Discount Code (Optional)')}
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="discountCode"
                  name="discountCode"
                  value={formData.discountCode}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-md border ${
                    errors.discountCode ? 'border-error' : discountApplied ? 'border-success' : 'border-border'
                  } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-premium`}
                  placeholder={t('أدخل كود الخصم إن وجد', 'Enter discount code if available')}
                />
                {discountApplied && (
                  <div className="absolute left-3 top-1/2 -translate-y-1/2">
                    <Icon name="CheckCircleIcon" size={20} className="text-success" />
                  </div>
                )}
              </div>
              {errors.discountCode && (
                <p className="mt-1 text-sm text-error flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {errors.discountCode}
                </p>
              )}
              {discountApplied && (() => {
                const appliedCode = formData.discountCode.trim().toUpperCase();
                if ((appliedCode === 'LABAN28' || appliedCode === 'GWDA77') && formData.cardType === 'Corporate') {
                  const count = Math.max(1, formData.employeeCount || 1);
                  return (
                    <p className="mt-1 text-sm text-success flex items-center gap-1">
                      <Icon name="CheckCircleIcon" size={16} />
                      {t(`تم تطبيق خصم 50 ريال على كل فرد! (${count} × 50 = ${count * 50} ريال خصم)`, `50 SAR discount applied per person! (${count} × 50 = ${count * 50} SAR discount)`)}
                    </p>
                  );
                } else if (appliedCode === 'EBRAHIM444' && formData.cardType === 'Family') {
                  return (
                    <p className="mt-1 text-sm text-success flex items-center gap-1">
                      <Icon name="CheckCircleIcon" size={16} />
                      {t('تم تطبيق خصم 550 ريال على بطاقة العائلة!', '550 SAR discount applied to Family card!')}
                    </p>
                  );
                } else if (formData.cardType === 'Personal') {
                  return (
                    <p className="mt-1 text-sm text-success flex items-center gap-1">
                      <Icon name="CheckCircleIcon" size={16} />
                      {t('تم تطبيق خصم 50 ريال على البطاقة الشخصية!', '50 SAR discount applied to Personal card!')}
                    </p>
                  );
                } else {
                  return (
                    <p className="mt-1 text-sm text-success flex items-center gap-1">
                      <Icon name="CheckCircleIcon" size={16} />
                      {t('تم تطبيق خصم 50% على السعر!', '50% discount applied to the price!')}
                    </p>
                  );
                }
              })()}
            </div>

            {/* Pricing Summary */}
            <div className="bg-muted/50 rounded-lg p-4 border border-border">
              <div className="flex justify-between items-center mb-2">
                <span className="text-muted-foreground">
                  {formData.cardType === 'Corporate'
                    ? `${t('السعر', 'Price')} (${Math.max(1, formData.employeeCount || 1)} ${t('موظف × 150 ريال', 'employees × 150 SAR')}):`
                    : t('السعر الأصلي:', 'Original Price:')}
                </span>
                <span className="font-bold text-foreground">{effectivePrice} {t('ريال', 'SAR')}</span>
              </div>
              {discountApplied && (() => {
                const appliedCode = formData.discountCode.trim().toUpperCase();
                let discountAmt = 0;
                let discountLabel = '';
                if ((appliedCode === 'LABAN28' || appliedCode === 'GWDA77') && formData.cardType === 'Corporate') {
                  const count = Math.max(1, formData.employeeCount || 1);
                  discountAmt = count * 50;
                  discountLabel = t(`الخصم (50 ريال × ${count} فرد):`, `Discount (50 SAR × ${count} persons):`);
                } else if (appliedCode === 'EBRAHIM444' && formData.cardType === 'Family') {
                  discountAmt = 550;
                  discountLabel = t('الخصم (550 ريال):', 'Discount (550 SAR):');
                } else if (formData.cardType === 'Personal') {
                  discountAmt = 50;
                  discountLabel = t('الخصم (50 ريال):', 'Discount (50 SAR):');
                } else {
                  discountAmt = Math.round(effectivePrice * 0.5);
                  discountLabel = t('الخصم (50%):', 'Discount (50%):');
                }
                const finalAmt = effectivePrice - discountAmt;
                return (
                  <>
                    <div className="flex justify-between items-center mb-2 text-success">
                      <span>{discountLabel}</span>
                      <span className="font-bold">- {discountAmt} {t('ريال', 'SAR')}</span>
                    </div>
                    <div className="border-t border-border pt-2 mt-2">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold text-foreground">{t('المبلغ النهائي:', 'Final Amount:')}</span>
                        <span className="text-2xl font-bold text-accent">{finalAmt} {t('ريال', 'SAR')}</span>
                      </div>
                    </div>
                  </>
                );
              })()}
              {!discountApplied && (
                <div className="border-t border-border pt-2 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-foreground">{t('المبلغ النهائي:', 'Final Amount:')}</span>
                    <span className="text-2xl font-bold text-accent">{effectivePrice} {t('ريال', 'SAR')}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Terms and Conditions */}
            <div className="p-4 bg-muted rounded-lg border border-border">
              <div className="flex items-start gap-3">
                <Icon name="InformationCircleIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                <div className="text-sm text-muted-foreground space-y-1">
                  {formData.cardType === 'Corporate' ? (
                    <>
                      <p>• {t('يمكن تحديد عدد الموظفين بحرية حسب احتياج الشركة', 'Number of employees can be freely determined based on company needs')}</p>
                      <p>• {t('سعر الفرد 150 ريال سنوياً', 'Price per person is 150 SAR annually')}</p>
                    </>
                  ) : (
                    <p>• {t('يمكن استخدام البطاقة من قبل 6 أشخاص فقط', 'Card can be used by 6 people only')}</p>
                  )}
                  <p>• {t('يجب إبراز البطاقة عند الاستفادة من الخصم', 'Card must be presented when using the discount')}</p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 bg-accent text-accent-foreground rounded-lg font-bold text-lg transition-premium hover:bg-accent/90 hover:shadow-lg active-press flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Icon name="ArrowPathIcon" size={24} className="animate-spin" />
                  <span>{t('جاري التسجيل...', 'Registering...')}</span>
                </>
              ) : (
                <>
                  <Icon name="CheckCircleIcon" size={24} />
                  <span>{t('تأكيد الاشتراك', 'Confirm Subscription')}</span>
                </>
              )}
            </button>

            {/* Back Button */}
            <button
              type="button"
              onClick={() => router.back()}
              className="w-full py-3 bg-muted text-foreground rounded-lg font-medium transition-premium hover:bg-muted/80 flex items-center justify-center gap-2"
            >
              <Icon name="ArrowRightIcon" size={20} />
              <span>{t('العودة للخلف', 'Go Back')}</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionRegistrationInteractive;